import React from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Check, ChevronRight, MoreHorizontal, Search, Navigation, Compass, Car, Radio } from "lucide-react";
import { useLang } from "../lib/i18n";

const LIVE_FEATURES = [
  { key: "assistant", icon: Compass, href: "/app" },
  { key: "save-car", icon: Car, href: "/app?open=save-car" },
  { key: "community", icon: Radio, href: "/app" },
];

/* ===== Phone Frame ===== */
const PhoneFrame = ({ children, label, testId, featured = false }) => (
  <div className="relative flex flex-col items-center" data-testid={testId}>
    <div
      className="relative"
      role="img"
      aria-label={`${label?.title || "SpotDrop"} app preview`}
    >
      {/* Glow */}
      <div className="absolute -inset-6 bg-[#ccff00]/[0.08] blur-[40px] rounded-full pointer-events-none" />
      {/* Phone shell */}
      <div
        className={`relative rounded-[44px] bg-[#1a1a1d] border border-white/10 p-[6px] shadow-[0_30px_80px_rgba(0,0,0,0.6)] ${
          featured ? "w-[280px] sm:w-[330px]" : "w-[230px] sm:w-[260px]"
        }`}
      >
        <div className="relative rounded-[38px] overflow-hidden bg-[#0a0a0c] aspect-[9/19.5]">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[100px] h-[26px] bg-black rounded-full z-30" />
          {/* Status bar */}
          <div className="absolute top-0 inset-x-0 h-[34px] flex items-center justify-between px-7 text-[10px] font-medium text-white z-20">
            <span>9:41</span>
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-2 bg-white rounded-sm" />
            </span>
          </div>
          {/* Screen content — non-interactive preview */}
          <div
            className="absolute inset-0 pt-10 select-none"
            aria-hidden="true"
            inert
          >
            {children}
          </div>
        </div>
      </div>
      {/* Preview badge */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-40 px-2.5 py-1 rounded-full bg-[#0a0a0c] border border-[#ccff00]/40 text-[9px] uppercase tracking-[0.2em] text-[#ccff00] font-semibold whitespace-nowrap shadow-[0_0_20px_rgba(204,255,0,0.15)]">
        App preview
      </div>
    </div>
    {label && (
      <div className="mt-6 text-center max-w-[260px]">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
          {label.tag}
        </div>
        <div className={`font-heading mt-1.5 ${featured ? "text-xl md:text-2xl font-semibold" : "text-base font-medium text-zinc-300"}`}>
          {label.title}
        </div>
      </div>
    )}
  </div>
);

/* ===== Mockup 1: Live Parking Map ===== */
const MapMockup = () => (
  <div className="relative w-full h-full bg-[#0d1410] overflow-hidden">
    {/* Map grid */}
    <svg className="absolute inset-0 w-full h-full opacity-60" viewBox="0 0 280 580" preserveAspectRatio="none">
      <defs>
        <pattern id="streets" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M0 20 L40 20 M20 0 L20 40" stroke="#1d2820" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="280" height="580" fill="url(#streets)" />
      {/* Coastline / roads */}
      <path d="M-10 120 Q 80 180 160 140 T 300 180" stroke="#2a3a2f" strokeWidth="2" fill="none" />
      <path d="M-10 300 Q 100 260 180 320 T 300 290" stroke="#2a3a2f" strokeWidth="2" fill="none" />
      <path d="M40 -10 Q 80 120 120 200 T 160 580" stroke="#243029" strokeWidth="1.5" fill="none" />
      <path d="M220 -10 Q 200 140 240 280 T 200 580" stroke="#243029" strokeWidth="1.5" fill="none" />
      {/* Water blob */}
      <path d="M180 380 Q 240 360 280 400 L 280 580 L 160 580 Q 140 440 180 380 Z" fill="#0a141c" opacity="0.7" />
    </svg>

    {/* Header */}
    <div className="absolute top-1 inset-x-0 px-4 py-3 flex items-center justify-between z-10">
      <div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Live now</div>
        <div className="text-[13px] font-semibold text-white">Sliema, Malta</div>
      </div>
      <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
        <Search className="w-3.5 h-3.5 text-zinc-400" />
      </div>
    </div>

    {/* Parking pins */}
    <div className="absolute top-[22%] left-[18%] z-10">
      <div className="relative">
        <div className="absolute -inset-2 bg-[#ccff00]/40 blur-md rounded-full animate-pulse" />
        <div className="relative w-4 h-4 rounded-full bg-[#ccff00] border-2 border-[#0d1410]" />
      </div>
      <div className="mt-1 -translate-x-1/3 px-2 py-1 rounded-md bg-[#0a0a0c]/90 border border-white/10 backdrop-blur whitespace-nowrap">
        <span className="text-[9px] font-semibold text-[#ccff00]">Leaving in 5 min</span>
      </div>
    </div>

    <div className="absolute top-[42%] right-[18%] z-10">
      <div className="relative">
        <div className="absolute -inset-2 bg-[#ccff00]/30 blur-md rounded-full animate-pulse" />
        <div className="relative w-3.5 h-3.5 rounded-full bg-[#ccff00] border-2 border-[#0d1410]" />
      </div>
      <div className="mt-1 -translate-x-2/3 px-2 py-1 rounded-md bg-[#0a0a0c]/90 border border-white/10 backdrop-blur whitespace-nowrap">
        <span className="text-[9px] font-semibold text-zinc-200">Leaving in 10 min</span>
      </div>
    </div>

    <div className="absolute top-[32%] left-[55%] z-10">
      <div className="w-3 h-3 rounded-full bg-[#ccff00]/60 border border-[#0d1410]" />
    </div>
    <div className="absolute top-[60%] left-[30%] z-10">
      <div className="w-3 h-3 rounded-full bg-[#ccff00]/50 border border-[#0d1410]" />
    </div>

    {/* User dot */}
    <div className="absolute top-[50%] left-[42%] z-10">
      <div className="relative">
        <div className="absolute -inset-3 bg-blue-400/30 blur-md rounded-full animate-ping" />
        <div className="relative w-3.5 h-3.5 rounded-full bg-blue-400 border-2 border-[#0d1410]" />
      </div>
    </div>

    {/* Bottom card */}
    <div className="absolute inset-x-3 bottom-3 z-20">
      <div className="rounded-2xl bg-[#141416]/95 backdrop-blur border border-white/10 p-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#ccff00] flex items-center justify-center shrink-0">
            <MapPin className="w-4 h-4 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-white">Spot available nearby</div>
            <div className="text-[9px] text-zinc-400 mt-0.5 flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" /> 90m away · 5 min walk
            </div>
          </div>
          <div className="w-7 h-7 rounded-full bg-[#ccff00] flex items-center justify-center shrink-0">
            <Navigation className="w-3 h-3 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ===== Mockup 2: Drop a Spot ===== */
const DropMockup = () => {
  const [stage, setStage] = React.useState(0); // 0 = pick time, 1 = confirmed
  const [selected, setSelected] = React.useState(5);
  const timer = React.useRef(null);

  React.useEffect(() => {
    const loop = () => {
      setStage(0);
      timer.current = setTimeout(() => setStage(1), 2200);
      timer.current = setTimeout(() => {
        setStage(0);
        loop();
      }, 5000);
    };
    loop();
    return () => clearTimeout(timer.current);
  }, []);

  return (
    <div className="relative w-full h-full bg-[#0a0a0c] flex flex-col">
      {/* Header */}
      <div className="px-4 pt-2 pb-3 flex items-center justify-between">
        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
          <ChevronRight className="w-3.5 h-3.5 text-zinc-500 rotate-180" />
        </div>
        <div className="text-[11px] font-semibold text-white">Drop a spot</div>
        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
          <MoreHorizontal className="w-3.5 h-3.5 text-zinc-500" />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col items-stretch justify-center px-4 pb-6">
        {stage === 0 ? (
          <>
            <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 text-center">Spot type</div>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              <div className="px-2 py-1.5 rounded-lg bg-[#ccff00] text-[#0a0a0c] text-[10px] font-semibold text-center">
                Normal
              </div>
              <div className="px-2 py-1.5 rounded-lg bg-blue-500/15 border border-blue-400/40 text-blue-300 text-[10px] font-semibold text-center inline-flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                Blue Badge only
              </div>
              <div className="px-2 py-1.5 rounded-lg bg-[#141416] border border-white/10 text-zinc-400 text-[10px] font-semibold text-center">
                Loading bay
              </div>
              <div className="px-2 py-1.5 rounded-lg bg-[#141416] border border-white/10 text-zinc-400 text-[10px] font-semibold text-center">
                Motorcycle
              </div>
            </div>

            <div className="mt-5 text-[9px] uppercase tracking-[0.22em] text-zinc-500 text-center">When?</div>
            <div className="font-heading text-lg font-semibold text-white mt-1 text-center leading-tight">
              I'm leaving in
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 w-full">
              {[5, 10, 15].map((m) => (
                <button
                  key={m}
                  type="button"
                  tabIndex={-1}
                  onClick={() => setSelected(m)}
                  className={`h-12 rounded-xl border transition-all ${
                    selected === m
                      ? "bg-[#ccff00] border-[#ccff00] text-[#0a0a0c]"
                      : "bg-[#141416] border-white/10 text-zinc-400"
                  }`}
                >
                  <div className="text-sm font-bold">{m}</div>
                  <div className="text-[8px] uppercase tracking-wider opacity-80">min</div>
                </button>
              ))}
            </div>

            <button type="button" tabIndex={-1} className="mt-4 w-full h-11 rounded-full bg-[#ccff00] text-[#0a0a0c] font-bold text-sm flex items-center justify-center gap-1.5">
              I'm leaving
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </button>

            <div className="mt-2 text-[9px] text-zinc-600 text-center">
              Nearby drivers will see it on the map
            </div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col items-center"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-[#ccff00]/30 blur-2xl rounded-full" />
              <div className="relative w-16 h-16 rounded-full bg-[#ccff00] flex items-center justify-center">
                <Check className="w-8 h-8 text-[#0a0a0c]" strokeWidth={3} />
              </div>
            </div>
            <div className="font-heading text-xl font-bold text-white mt-5">Spot dropped</div>
            <div className="text-[11px] text-zinc-400 mt-1.5 text-center max-w-[180px]">
              Nearby drivers can see your spot on the map.
            </div>
            <div className="mt-5 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ccff00]/10 border border-[#ccff00]/30">
              <Clock className="w-3 h-3 text-[#ccff00]" />
              <span className="text-[10px] text-[#ccff00] font-semibold">
                Leaving in {selected} min
              </span>
            </div>
          </motion.div>
        )}
      </div>

      {/* Home indicator */}
      <div className="pb-2 flex justify-center">
        <div className="w-24 h-1 bg-white/20 rounded-full" />
      </div>
    </div>
  );
};

/* ===== Mockup 3: Handover (I can wait / I can't wait) ===== */
const HandoverMockup = () => {
  const [mode, setMode] = React.useState("wait");

  React.useEffect(() => {
    const id = setInterval(() => {
      setMode((m) => (m === "wait" ? "cant" : "wait"));
    }, 3500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative w-full h-full bg-[#0a0a0c] flex flex-col">
      {/* Header */}
      <div className="px-4 pt-2 pb-3 flex items-center justify-between">
        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
          <ChevronRight className="w-3.5 h-3.5 text-zinc-500 rotate-180" />
        </div>
        <div className="text-[11px] font-semibold text-white">Handover</div>
        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
          <MoreHorizontal className="w-3.5 h-3.5 text-zinc-500" />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col items-stretch justify-start px-4 pb-6">
        {/* Arriving driver card */}
        <div className="mt-2 p-3 rounded-2xl bg-[#141416] border border-white/10">
          <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Arriving driver
          </div>
          <div className="mt-2 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#ccff00]/10 border border-[#ccff00]/30 flex items-center justify-center shrink-0">
              <MapPin className="w-4 h-4 text-[#ccff00]" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-white truncate">
                Luca · White Fiat 500
              </div>
              <div className="text-[10px] text-zinc-400 mt-0.5 truncate">
                Plate JFK-224 · 120m away
              </div>
            </div>
          </div>
        </div>

        {/* Prompt */}
        <div className="mt-5 text-[9px] uppercase tracking-[0.22em] text-zinc-500 text-center">
          Can you wait?
        </div>
        <div className="font-heading text-base font-semibold text-white mt-1 text-center leading-tight">
          Tell them what to expect
        </div>

        {/* Options */}
        <div className="mt-4 grid grid-cols-1 gap-2 w-full">
          <div
            className={`p-3 rounded-2xl border transition-all ${
              mode === "wait"
                ? "bg-[#ccff00] border-[#ccff00]"
                : "bg-[#141416] border-white/10"
            }`}
          >
            <div className="flex items-center gap-2">
              <Check className={`w-4 h-4 ${mode === "wait" ? "text-[#0a0a0c]" : "text-zinc-500"}`} strokeWidth={2.5} />
              <div className={`text-sm font-bold ${mode === "wait" ? "text-[#0a0a0c]" : "text-white"}`}>
                I can wait
              </div>
            </div>
            <div className={`text-[10px] mt-1 ml-6 ${mode === "wait" ? "text-[#0a0a0c]/70" : "text-zinc-500"}`}>
              I&apos;ll stay until Luca arrives.
            </div>
          </div>
          <div
            className={`p-3 rounded-2xl border transition-all ${
              mode === "cant"
                ? "bg-[#ccff00] border-[#ccff00]"
                : "bg-[#141416] border-white/10"
            }`}
          >
            <div className="flex items-center gap-2">
              <Clock className={`w-4 h-4 ${mode === "cant" ? "text-[#0a0a0c]" : "text-zinc-500"}`} strokeWidth={2.5} />
              <div className={`text-sm font-bold ${mode === "cant" ? "text-[#0a0a0c]" : "text-white"}`}>
                I can&apos;t wait
              </div>
            </div>
            <div className={`text-[10px] mt-1 ml-6 ${mode === "cant" ? "text-[#0a0a0c]/70" : "text-zinc-500"}`}>
              Leaving now — first driver takes it.
            </div>
          </div>
        </div>

        <div className="mt-4 text-[9px] text-zinc-600 text-center px-4">
          Honest handovers keep the map fair for everyone.
        </div>
      </div>

      {/* Home indicator */}
      <div className="pb-2 flex justify-center">
        <div className="w-24 h-1 bg-white/20 rounded-full" />
      </div>
    </div>
  );
};

/* ===== Showcase Section ===== */
export default function AppShowcase() {
  const { t } = useLang();
  const screens = [
    {
      key: "map",
      label: { tag: t("showcase.mockup1.tag"), title: t("showcase.mockup1.title") },
      content: <MapMockup />,
      delay: 0,
    },
    {
      key: "drop",
      label: { tag: t("showcase.mockup2.tag"), title: t("showcase.mockup2.title") },
      content: <DropMockup />,
      delay: 0.1,
    },
    {
      key: "handover",
      label: { tag: t("showcase.mockup3.tag"), title: t("showcase.mockup3.title") },
      content: <HandoverMockup />,
      delay: 0.2,
    },
  ];

  return (
    <section id="showcase" className="relative py-20 md:py-32 border-t border-white/5 overflow-hidden">
      {/* ambient */}
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[60%] bg-[#ccff00]/[0.04] blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
            {t("showcase.eyebrow")}
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-3 md:mt-4">
            {t("showcase.title")}
          </h2>
          <p className="text-zinc-400 mt-3 md:mt-4 text-base md:text-lg">
            {t("showcase.sub")}
          </p>
        </motion.div>

        <div className="mt-12 md:mt-20 flex flex-wrap items-end justify-center gap-10 md:gap-8 lg:gap-12">
          {screens.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: s.delay, ease: [0.22, 1, 0.36, 1] }}
              className={i === 0 ? "order-first md:order-none" : ""}
            >
              <PhoneFrame label={s.label} testId={`mockup-${s.key}`} featured={i === 0}>
                {s.content}
              </PhoneFrame>
            </motion.div>
          ))}
        </div>

        {/* What drivers can actually use today — the three live features. */}
        <div className="mt-16 md:mt-24" data-testid="live-features">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-[#ccff00]/40 bg-[#ccff00]/10 text-[9px] uppercase tracking-[0.2em] text-[#ccff00] font-semibold">
              <span className="w-1 h-1 rounded-full bg-[#ccff00] animate-pulse" />
              {t("showcase.liveNow")}
            </span>
            <h3 className="font-heading text-base md:text-lg font-medium">
              {t("showcase.liveTitle")}
            </h3>
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {LIVE_FEATURES.map((f, i) => (
              <a
                key={f.key}
                href={f.href}
                className="group p-5 rounded-2xl bg-[#141416] border border-white/10 hover:border-[#ccff00]/40 transition-colors"
                data-testid={`live-feature-${f.key}`}
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/20">
                  <f.icon className="w-4.5 h-4.5" />
                </div>
                <div className="font-heading text-lg font-medium mt-4">
                  {t(`showcase.live${i + 1}.title`)}
                </div>
                <p className="text-zinc-400 text-sm mt-1.5 leading-relaxed">
                  {t(`showcase.live${i + 1}.desc`)}
                </p>
                <span className="inline-flex items-center gap-1 text-[13px] text-[#ccff00] font-semibold mt-3">
                  {t(`showcase.live${i + 1}.cta`)}
                  <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-14 md:mt-20 flex flex-col items-center gap-3 text-center">
          <p className="text-xs md:text-sm text-zinc-500 max-w-sm">
            {t("showcase.ctaCopy")}
          </p>
          <a href="/app" data-testid="showcase-cta">
            <button
              type="button"
              className="group bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold rounded-full px-6 h-11 text-sm inline-flex items-center transition-colors"
            >
              {t("showcase.ctaBtn")}
              <svg className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
          </a>
        </div>
      </div>
    </section>
  );
}
